import { useCallback, useState, useRef, useEffect } from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import classNames from 'classnames';

import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import { BUTTON_SIZE, BUTTON_TYPE, Button } from '../Button';
import InputPin from '../InputPin';
import {
  sendPhoneOtp,
  confirmPhoneOtp,
  saveCurrentAuthToken,
  restoreAuthSession,
  clearRecaptchaVerifier,
} from '../../../../utils/auth';
import {
  postRegisterPhone,
  postPhoneVerify,
  postMfaEnable,
} from '../../utils/service';
import {
  isInvalidVerificationCodeError,
  isCodeExpiredError,
  isPhoneAlreadyLinkedError,
} from '../../utils/errors';

import './PhoneVerification.scss';

const STEPS = {
  PHONE_INPUT: 'PHONE_INPUT',
  CODE_INPUT: 'CODE_INPUT',
  WAITING_OTHER_TAB: 'WAITING_OTHER_TAB',
};

const RESEND_COUNTDOWN_SECONDS = 60;

const BROADCAST_CHANNEL_NAME = 'phone-verification';
const BROADCAST_MSG = {
  MOUNTED: 'MOUNTED',
  OTP_ACTIVE: 'OTP_ACTIVE',
  COMPLETED: 'COMPLETED',
  RELEASED: 'RELEASED',
  TAKEOVER: 'TAKEOVER',
  SKIPPED: 'SKIPPED',
};
const WAITING_TIMEOUT_MS = 5 * 60 * 1000;

const PhoneVerification = ({
  initialPhone,
  onComplete,
  onSkip,
  onPhoneFlowActive,
  className,
}) => {
  const {
    pauseAuthListener, resumeAuthListener, reloadUserData,
  } = useFirebaseAuth();

  const [step, setStep] = useState(STEPS.PHONE_INPUT);
  const [phoneNumber, setPhoneNumber] = useState(initialPhone || '');
  const [pinValues, setPinValues] = useState(Array(6).fill(''));
  const [isValidPin, setIsValidPin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const resendTimerRef = useRef(null);
  const confirmationResultRef = useRef(null);
  const savedAuthTokenRef = useRef(null);
  const restoreTokenRef = useRef(null);
  const authListenerPausedRef = useRef(false);
  // Tracks whether this instance has called sendPhoneOtp. Only the instance that
  // initiated the verifier should clear it on unmount — prevents clobbering an
  // active OTP flow in another mounted PhoneVerification (e.g. gift-card modal
  // briefly overlapping with the login flow during navigation).
  const verifierOwnedRef = useRef(false);
  const autoSendAttemptedRef = useRef(false);
  const channelRef = useRef(null);
  const tabIdRef = useRef(`${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const otpFlowActiveRef = useRef(false);
  const blockedByOtherTabRef = useRef(false);
  const waitingTimeoutRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const reloadUserDataRef = useRef(reloadUserData);
  const resumeAuthListenerRef = useRef(resumeAuthListener);
  const onSkipRef = useRef(onSkip);
  onCompleteRef.current = onComplete;
  reloadUserDataRef.current = reloadUserData;
  resumeAuthListenerRef.current = resumeAuthListener;
  onSkipRef.current = onSkip;

  const startResendCountdown = useCallback(() => {
    setResendCountdown(RESEND_COUNTDOWN_SECONDS);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          resendTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendOtp = useCallback(async () => {
    // [FAILURE POINT 9] If another tab is already running OTP, bail out
    // to avoid concurrent Firebase phone auth flows.
    if (blockedByOtherTabRef.current) {
      return;
    }

    setErrorMessage('');
    setIsLoading(true);
    otpFlowActiveRef.current = true;

    try {
      const fullPhone = `+1${phoneNumber.replace(/\D/g, '')}`;
      const parsed = parsePhoneNumber(fullPhone, 'US');

      if (!parsed || !parsed.isValid()) {
        otpFlowActiveRef.current = false;
        setErrorMessage('Please enter a valid phone number.');
        setIsLoading(false);
        return;
      }

      if (!savedAuthTokenRef.current) {
        savedAuthTokenRef.current = await saveCurrentAuthToken();
      }

      const { data: registerData, error: registerError } = await postRegisterPhone({
        phoneNumber: phoneNumber.replace(/\D/g, ''),
        countryCode: 'US',
        authToken: savedAuthTokenRef.current,
      });

      if (registerError) {
        throw new Error(registerError?.message || registerError);
      }

      if (registerData?.customToken) {
        restoreTokenRef.current = registerData.customToken;
      }

      // [FAILURE POINT 10] Re-check after async calls — another tab may
      // have claimed the OTP flow while we were awaiting the API.
      if (blockedByOtherTabRef.current) {
        otpFlowActiveRef.current = false;
        setIsLoading(false);
        return;
      }

      pauseAuthListener();
      authListenerPausedRef.current = true;
      onPhoneFlowActive?.(true);

      verifierOwnedRef.current = true;
      const result = await sendPhoneOtp(parsed.number);
      confirmationResultRef.current = result;
      setStep(STEPS.CODE_INPUT);
      startResendCountdown();
    } catch (err) {
      otpFlowActiveRef.current = false;
      try {
        channelRef.current?.postMessage({ type: BROADCAST_MSG.RELEASED });
      } catch { /* ignore */ }
      if (authListenerPausedRef.current) {
        if (restoreTokenRef.current) {
          await restoreAuthSession(restoreTokenRef.current);
        }
        resumeAuthListener();
        authListenerPausedRef.current = false;
        onPhoneFlowActive?.(false);
      }
      setErrorMessage('Failed to send verification code. Please try again or use a different phone number.');
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, pauseAuthListener, resumeAuthListener, onPhoneFlowActive, startResendCountdown]);

  const handleResendCode = useCallback(async () => {
    setErrorMessage('');
    setIsValidPin(true);
    setPinValues(Array(6).fill(''));
    setIsLoading(true);

    try {
      const fullPhone = `+1${phoneNumber.replace(/\D/g, '')}`;
      const result = await sendPhoneOtp(fullPhone);
      confirmationResultRef.current = result;
      startResendCountdown();
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, startResendCountdown]);

  const handleValidateCode = useCallback(async () => {
    const code = pinValues.join('');

    if (code.length !== 6) {
      setIsValidPin(false);
      setErrorMessage('Please enter the full 6-digit code.');
      return;
    }

    setErrorMessage('');
    setIsValidPin(true);
    setIsLoading(true);

    try {
      const savedToken = savedAuthTokenRef.current;

      const idToken = await confirmPhoneOtp(confirmationResultRef.current, code);

      const { error: verifyError } = await postPhoneVerify({
        idToken,
        authToken: savedToken,
      });

      if (verifyError) {
        if (restoreTokenRef.current) {
          await restoreAuthSession(restoreTokenRef.current);
        }
        resumeAuthListener();
        authListenerPausedRef.current = false;
        onPhoneFlowActive?.(false);
        setErrorMessage(verifyError?.message || 'Phone verification failed. Please try again.');
        setIsLoading(false);
        return;
      }

      const { data: mfaData, error: mfaError } = await postMfaEnable({ authToken: savedToken });

      if (mfaError) {
        if (restoreTokenRef.current) {
          await restoreAuthSession(restoreTokenRef.current);
        }
        resumeAuthListener();
        authListenerPausedRef.current = false;
        onPhoneFlowActive?.(false);
        setErrorMessage(mfaError?.message || 'Failed to enable verification. Please try again.');
        setIsLoading(false);
        return;
      }

      await restoreAuthSession(mfaData.customToken);
      resumeAuthListener();
      authListenerPausedRef.current = false;
      onPhoneFlowActive?.(false);
      await reloadUserData();

      otpFlowActiveRef.current = false;
      try {
        channelRef.current?.postMessage({ type: BROADCAST_MSG.COMPLETED });
      } catch { /* ignore */ }

      onComplete?.();
    } catch (err) {
      if (isInvalidVerificationCodeError(err)) {
        setIsValidPin(false);
        setErrorMessage('Invalid code. Verify it and try again or get a new code.');
      } else if (isCodeExpiredError(err)) {
        setIsValidPin(false);
        setErrorMessage('Code expired. Please request a new code.');
      } else {
        if (authListenerPausedRef.current) {
          if (restoreTokenRef.current) {
            await restoreAuthSession(restoreTokenRef.current);
          }
          resumeAuthListener();
          authListenerPausedRef.current = false;
          onPhoneFlowActive?.(false);
        }
        if (isPhoneAlreadyLinkedError(err)) {
          setErrorMessage('This phone number is already linked to another account.');
        } else {
          setErrorMessage(err?.message || 'Verification failed. Please try again.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [pinValues, resumeAuthListener, reloadUserData, onPhoneFlowActive, onComplete]);

  useEffect(() => () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }
    if (verifierOwnedRef.current) {
      clearRecaptchaVerifier();
    }
    if (authListenerPausedRef.current) {
      if (restoreTokenRef.current) {
        restoreAuthSession(restoreTokenRef.current);
      }
      resumeAuthListener();
      authListenerPausedRef.current = false;
      onPhoneFlowActive?.(false);
    }
  }, [resumeAuthListener, onPhoneFlowActive]);

  // [FAILURE POINT 7] Cross-tab coordination via BroadcastChannel.
  // On mount, announces itself. If another tab responds with OTP_ACTIVE,
  // enters WAITING_OTHER_TAB. Falls back gracefully if BroadcastChannel
  // is unavailable — both tabs work independently.
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
      return undefined;
    }

    channelRef.current = channel;

    channel.onmessage = (event) => {
      const { type } = event.data || {};

      switch (type) {
        case BROADCAST_MSG.MOUNTED:
          if (otpFlowActiveRef.current) {
            channel.postMessage({ type: BROADCAST_MSG.OTP_ACTIVE });
          }
          break;

        case BROADCAST_MSG.OTP_ACTIVE:
          if (!otpFlowActiveRef.current) {
            blockedByOtherTabRef.current = true;
            setStep(STEPS.WAITING_OTHER_TAB);
            clearTimeout(waitingTimeoutRef.current);
            // [FAILURE POINT 8] Timeout fallback — if the active tab
            // crashes or closes without broadcasting, this tab recovers
            // after 5 minutes and re-enables controls.
            waitingTimeoutRef.current = setTimeout(() => {
              blockedByOtherTabRef.current = false;
              setStep(STEPS.PHONE_INPUT);
            }, WAITING_TIMEOUT_MS);
          }
          break;

        case BROADCAST_MSG.COMPLETED:
          if (blockedByOtherTabRef.current) {
            clearTimeout(waitingTimeoutRef.current);
            blockedByOtherTabRef.current = false;
            reloadUserDataRef.current().then(() => onCompleteRef.current?.());
          }
          break;

        case BROADCAST_MSG.RELEASED:
          if (blockedByOtherTabRef.current) {
            clearTimeout(waitingTimeoutRef.current);
            blockedByOtherTabRef.current = false;
            setStep(STEPS.PHONE_INPUT);
          }
          break;

        case BROADCAST_MSG.SKIPPED:
          clearTimeout(waitingTimeoutRef.current);
          blockedByOtherTabRef.current = false;
          onSkipRef.current?.();
          break;

        case BROADCAST_MSG.TAKEOVER:
          if (event.data?.senderId === tabIdRef.current) {
            break;
          }

          blockedByOtherTabRef.current = true;
          setStep(STEPS.WAITING_OTHER_TAB);
          setIsLoading(false);
          clearTimeout(waitingTimeoutRef.current);
          waitingTimeoutRef.current = setTimeout(() => {
            blockedByOtherTabRef.current = false;
            setStep(STEPS.PHONE_INPUT);
          }, WAITING_TIMEOUT_MS);

          if (otpFlowActiveRef.current) {
            otpFlowActiveRef.current = false;
            // Async cleanup of the abandoned OTP flow — restore the auth
            // session and resume the listener so this tab's auth state is
            // clean while it waits.
            (async () => {
              if (resendTimerRef.current) {
                clearInterval(resendTimerRef.current);
                resendTimerRef.current = null;
              }
              if (verifierOwnedRef.current) {
                clearRecaptchaVerifier();
                verifierOwnedRef.current = false;
              }
              if (authListenerPausedRef.current) {
                try {
                  if (restoreTokenRef.current) {
                    await restoreAuthSession(restoreTokenRef.current);
                  }
                } catch { /* ignore */ }
                resumeAuthListenerRef.current();
                authListenerPausedRef.current = false;
              }
            })();
          }
          break;

        default:
          break;
      }
    };

    channel.postMessage({ type: BROADCAST_MSG.MOUNTED });
    channel.postMessage({ type: BROADCAST_MSG.TAKEOVER, senderId: tabIdRef.current });

    return () => {
      clearTimeout(waitingTimeoutRef.current);
      if (otpFlowActiveRef.current) {
        try { channel.postMessage({ type: BROADCAST_MSG.RELEASED }); } catch { /* ignore */ }
      }
      channel.close();
      channelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!initialPhone || autoSendAttemptedRef.current) return undefined;

    const digits = initialPhone.replace(/\D/g, '');
    if (digits.length < 10) return undefined;

    autoSendAttemptedRef.current = true;
    return undefined;
  }, []);

  const handleGoBackToPhone = useCallback(() => {
    if (otpFlowActiveRef.current) {
      otpFlowActiveRef.current = false;
      try {
        channelRef.current?.postMessage({ type: BROADCAST_MSG.RELEASED });
      } catch { /* ignore */ }
    }
    setStep(STEPS.PHONE_INPUT);
    setPinValues(Array(6).fill(''));
    setIsValidPin(true);
    setErrorMessage('');
    setResendCountdown(0);
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
  }, []);

  if (step === STEPS.WAITING_OTHER_TAB) {
    return (
      <div className={classNames('PhoneVerification', className)}>
        <div className="PhoneVerification__title">
          CONTINUE IN
          <br />
          PREVIOUS TAB
        </div>
        <div className="PhoneVerification__subtitle">
          Phone verification is active in the previous
          Trashie tab. You may close this one and continue the process there.
        </div>
      </div>
    );
  }

  if (step === STEPS.CODE_INPUT) {
    const formattedPhone = `+1 ${phoneNumber.replace(/^\+?1/, '').replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`;

    return (
      <div className={classNames('PhoneVerification', className)}>
        <div className="PhoneVerification__title">
          ENTER THE
          <br />
          6-DIGIT CODE
        </div>
        <div className="PhoneVerification__subtitle">
          {`Your code was sent to ${formattedPhone}`}
        </div>
        <InputPin
          pinValues={pinValues}
          setPinValues={setPinValues}
          isValidPin={isValidPin}
          errorMessage={errorMessage}
        />
        <div className="PhoneVerification__codeResend">
          {resendCountdown > 0 ? (
            <span className="PhoneVerification__codeResend--countdown">
              {`REQUEST NEW CODE IN ${resendCountdown} SECONDS`}
            </span>
          ) : (
            <button
              type="button"
              className="PhoneVerification__codeResend--link"
              onClick={handleResendCode}
              disabled={isLoading}
            >
              Request a New Code
            </button>
          )}
        </div>
        {errorMessage && isValidPin && (
          <div className="PhoneVerification__error">
            {errorMessage}
          </div>
        )}
        <Button
          disabled={isLoading || pinValues.join('').length !== 6}
          type={BUTTON_TYPE.QUATERNARY}
          size={BUTTON_SIZE.LARGE}
          onClick={handleValidateCode}
        >
          {isLoading ? 'VALIDATING...' : 'CONTINUE'}
        </Button>
        <Button
          type={BUTTON_TYPE.LINK}
          size={BUTTON_SIZE.LARGE}
          onClick={handleGoBackToPhone}
        >
          CHANGE NUMBER
        </Button>
      </div>
    );
  }

  return (
    <div className={classNames('PhoneVerification', className)}>
      <div className="PhoneVerification__title">
        VERIFY YOUR MOBILE
      </div>
      <div className="PhoneVerification__subtitle">
        This helps keep your account and rewards safe
      </div>
      <div className="PhoneVerification__phoneInput">
        <span className="PhoneVerification__phoneInput--prefix">+1</span>
        <input
          className="PhoneVerification__phoneInput--field ph-no-capture"
          type="tel"
          data-private="redact"
          value={phoneNumber}
          disabled={isLoading}
          onChange={(e) => {
            setPhoneNumber(e.target.value);
            setErrorMessage('');
          }}
        />
      </div>
      {errorMessage && (
        <div className="PhoneVerification__error">
          {errorMessage}
        </div>
      )}
      <Button
        disabled={isLoading || !phoneNumber.replace(/\D/g, '').length}
        type={BUTTON_TYPE.QUATERNARY}
        size={BUTTON_SIZE.LARGE}
        onClick={handleSendOtp}
      >
        {isLoading ? 'SENDING...' : 'SEND CODE'}
      </Button>
      {onSkip && (
        <Button
          disabled={isLoading}
          type={BUTTON_TYPE.QUINARY}
          size={BUTTON_SIZE.LARGE}
          onClick={() => {
            otpFlowActiveRef.current = false;
            try {
              channelRef.current?.postMessage({
                type: BROADCAST_MSG.SKIPPED, senderId: tabIdRef.current,
              });
            } catch { /* ignore */ }
            onSkip();
          }}
        >
          DO THIS LATER
        </Button>
      )}
    </div>
  );
};

export default PhoneVerification;
